import { useEffect, useRef } from 'react';
import { mapsApi } from '../../api/maps';
import { followJob as nativeFollowJob, nativeRunning } from '../../lib/backgroundPositions';

/** How often the carried job is asked for: picking a new one up within this. */
export const JOB_READ_MS = 15000;

/** How often the carrying rider's position is sent: the mobile plan's 5-second tier on a job. */
export const PING_MS = 5000;

const CARRYING = ['accepted', 'picked_up'];

/**
 * The carrying rider's position, every few seconds, to the delivery's journey
 * (InGo ADR 0013; the mobile plan's M6, foreground half): what the client's
 * map follows (`journeys/delivery/{id}/tracking`) and what the distance is
 * measured from. Uses the provider's one position watch (`liveRef`), never a
 * second one. A position is sent only when it is the phone's own, now
 * (`located`), never a cached one.
 *
 * Says what it does: a job found, positions sent, and why it stopped (the
 * journey not recording, the job gone), so "the client's map never moved"
 * has a line to read.
 *
 * With the phone locked or the app in the background the WebView stops
 * reading positions; there the native recorder posts them
 * (`lib/backgroundPositions.js`, M6's second half). It is told the carried job
 * here, and while it runs these pings stand down: one record, not two.
 *
 * @param {{ driverId: string | null, liveRef: { current: any }, located: boolean, work: { current: () => Promise<any> }, maps?: ReturnType<typeof import('../../api/maps').createMapsApi>, native?: { followJob: (id: any) => any, running: () => boolean } }} args
 */
export function useJourneyPings({ driverId, liveRef, located, work, maps = mapsApi, native = NATIVE }) {
  const jobRef = useRef(null);

  // The carried job, read on its own slow clock.
  useEffect(() => {
    if (!driverId) return undefined;
    let stopped = false;
    const read = () =>
      work.current().then(
        (job) => {
          if (stopped) return;
          const carrying = job && CARRYING.includes(job.status) ? { id: job.id, status: job.status } : null;
          if (carrying?.id !== jobRef.current?.id) {
            console.info(`[journey] ${carrying ? `carrying ${JSON.stringify(carrying)}` : 'no job carried'}`);
            // The native recorder follows the job too (it ignores this until the rider's own tap started it).
            void native.followJob(carrying?.id ?? null);
          }
          jobRef.current = carrying;
        },
        // Could not ask: keep what was known; the next read decides. Said, so
        // "it never ran" and "it ran and failed" do not leave the same nothing.
        (e) => console.warn(`[journey] carried job not read ${JSON.stringify({ code: e?.code || null, status: e?.status || 0, message: e?.message || String(e) })}`),
      );
    read();
    const id = window.setInterval(read, JOB_READ_MS);

    return () => {
      stopped = true;
      window.clearInterval(id);
      jobRef.current = null;
    };
  }, [driverId, work, native]);

  // The positions, while a job is carried and the phone knows where it is.
  useEffect(() => {
    if (!driverId || !located) return undefined;
    let stopped = false;
    const send = async () => {
      const job = jobRef.current;
      const { lat, lng, accuracy, headingDeg } = liveRef.current || {};
      if (stopped || !job || lat == null || lng == null) return;
      // The native recorder is posting this job's positions: one record, not two.
      if (native.running(job.id)) return;
      try {
        await maps.sendPositions(job.id, [
          {
            latitude: lat,
            longitude: lng,
            recorded_at: new Date().toISOString(),
            accuracy_metres: Number.isFinite(Number(accuracy)) ? Math.round(Number(accuracy)) : null,
            heading_degrees: Number.isFinite(Number(headingDeg)) ? Math.round(Number(headingDeg)) % 360 : null,
          },
        ]);
      } catch (e) {
        console.warn(`[journey] position not sent ${JSON.stringify({ delivery: job.id, code: e?.code || null, status: e?.status || 0 })}`);
        // The journey stopped recording, ended (a position after the completion, 409
        // JOURNEY_ENDED, 4 Oct 2026), or is not this rider's: stop until the job read says otherwise.
        if (e?.code === 'JOURNEY_NOT_RECORDING' || e?.code === 'JOURNEY_ENDED' || e?.status === 403 || e?.status === 404) jobRef.current = null;
      }
    };
    void send();
    const id = window.setInterval(() => void send(), PING_MS);

    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [driverId, located, liveRef, maps, native]);
}

const NATIVE = { followJob: nativeFollowJob, running: nativeRunning };
